// Words for wire codes, each through one door (design-system §3.9, D-441): a
// code the dictionary has no key for reads as the reader's "Other", never as
// the code itself.

import type { TFunction } from "i18next";
import { enumLabel } from "../../../../lib/enum-label";
import { RequestError } from "../../../../lib/request";
import type { AgentListItem } from "./agents-lib";

/** `agents.tools.<code>.name` (D-505 ruling 5). */
export function toolName(t: TFunction, toolCode: string): string {
  return enumLabel(t, "agents.tools", `${toolCode}.name`);
}

/** An agent's display name, else its catalog name (spec 14 §5 `GET /v1/agents`). */
export function agentName(
  t: TFunction,
  agent: Pick<AgentListItem, "display_name" | "agent_code">,
): string {
  if (agent.display_name !== null && agent.display_name.trim().length > 0) {
    return agent.display_name;
  }
  return enumLabel(t, "agents.catalog", `${agent.agent_code}.name`);
}

/** A problem code's sentence. */
export function problemText(t: TFunction, code: string | undefined): string {
  if (code === undefined) return t("agents.problems.other");
  const text = enumLabel(t, "agents.problems", code);
  return text === t("ref.unlisted") ? t("agents.problems.other") : text;
}

/** The sentence a failed call shows: its problem code's, else the generic refusal. */
export function errorText(t: TFunction, error: unknown): string {
  return problemText(t, error instanceof RequestError ? error.code : undefined);
}

/** The undo dialog's instructions for a compensatable action (D-511 ruling 2). */
export function compensationText(t: TFunction, key: string | null): string | undefined {
  if (!key?.startsWith("agents.compensation.")) return undefined;
  const text = enumLabel(t, "agents.compensation", key.slice("agents.compensation.".length));
  return text === t("ref.unlisted") ? undefined : text;
}

/** A run's outcome sentence (D-515 ruling 6); params interpolate. */
export function outcomeText(
  t: TFunction,
  outcome: { readonly key: string; readonly params?: Record<string, string | number> } | null,
): string | undefined {
  if (!outcome?.key.startsWith("agents.outcome.")) return undefined;
  const missing = "\u0000";
  const text = t(outcome.key, { ...(outcome.params ?? {}), defaultValue: missing });
  return text === missing || text.length === 0 ? t("ref.unlisted") : text;
}
